import { useState, useEffect, useCallback } from 'react';
import { getToken } from '../../../utils/token';
import { fetchExchangeRates } from '../services/exchangeRates.service';
import type { ExchangeRates } from '../types';

/**
 * Custom hook for fetching exchange rates for a given currency
 * @param currency - Currency code to fetch exchange rates for
 * @returns Object containing exchangeRates object, loading state, error state, and refetch function
 */
export const useExchangeRates = (currency: string) => {
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const loadExchangeRates = useCallback(async () => {
    if (!currency) {
      setExchangeRates(null);
      return;
    }

    const token = getToken();
    if (!token) {
      setExchangeRates(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const rates = await fetchExchangeRates(token, currency);
      setExchangeRates(rates);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch exchange rates'));
      setExchangeRates(null);
    } finally {
      setLoading(false);
    }
  }, [currency]);

  useEffect(() => {
    loadExchangeRates();
  }, [loadExchangeRates]);

  return {
    exchangeRates,
    loading,
    error,
    refetch: loadExchangeRates,
  };
};
